import React, { useEffect, useState } from "react";
import { http, fileUrl, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PortalHeader from "@/components/PortalHeader";
import StatusBadge from "@/components/StatusBadge";
import { toast } from "sonner";
import {
  Loader2, CheckCircle2, Clock, CalendarPlus, Send, Lock,
  CreditCard, ExternalLink, FileText, Image as ImageIcon, Plus, X,
  AlertTriangle, Phone, Video, MapPin
} from "lucide-react";

export default function ClientDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [messages, setMessages] = useState([]);
  const [tab, setTab] = useState("overview");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [dash, msgs] = await Promise.all([
        http.get("/client/dashboard"),
        http.get("/client/messages"),
      ]);
      setData(dash.data);
      setMessages(msgs.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  if (loading || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A120E]">
        <Loader2 className="w-8 h-8 animate-spin text-[#D2A853]" />
      </div>
    );
  }

  const status = data.user.status;
  const paid = data.payment?.status === "confirmed";
  const sessionType = data.user.session_type || null;
  const awaitingConsult = !sessionType && !paid; // consultation phase

  return (
    <div className="min-h-screen bg-[#0A120E]">
      <PortalHeader title="Client Portal" right={<StatusBadge status={status} />} />
      <div className="max-w-6xl mx-auto px-6 lg:px-12 py-10">
        {/* Greeting */}
        <div className="mb-10">
          <span className="overline text-[#D2A853]">Welcome back</span>
          <h1 className="font-display text-4xl sm:text-5xl text-[#E8E9E3] mt-2">{data.user.name}</h1>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-8 border-b border-[#D2A853]/15">
          <TabBtn active={tab === "overview"} onClick={() => setTab("overview")} testId="tab-overview">Overview</TabBtn>
          <TabBtn active={tab === "messages"} onClick={() => setTab("messages")} testId="tab-messages">Messages</TabBtn>
          <TabBtn active={tab === "settings"} onClick={() => setTab("settings")} testId="tab-settings">Account</TabBtn>
        </div>

        {tab === "overview" && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-8">
              {awaitingConsult ? (
                <ConsultationPendingCard settings={data.settings || {}} />
              ) : (
                <>
                  <DepositWidget data={data} reload={load} />
                  <BookingWidget data={data} paid={paid} reload={load} />
                </>
              )}
              <ReferencesWidget files={data.files} />
            </div>
            <div className="space-y-8">
              <ProjectCard intake={data.intake} />
              <NotesCard notes={data.notes} />
            </div>
          </div>
        )}

        {tab === "messages" && <MessagesPanel messages={messages} reload={load} />}
        {tab === "settings" && <AccountPanel user={data.user} />}
      </div>
    </div>
  );
}

function ConsultationPendingCard({ settings }) {
  const r = settings || {};
  return (
    <div className="frame-card p-8" data-testid="consultation-pending-card">
      <span className="overline text-[#D2A853]">Consultation Phase</span>
      <h3 className="font-display text-2xl text-[#E8E9E3] mt-2 mb-3">
        Your consultation is being scheduled
      </h3>
      <p className="text-sm text-[#A3A8A4] leading-relaxed mb-6">
        Brian will reach out shortly to set up a time to discuss your project. We&rsquo;ll go over your design,
        size, and final pricing during your consultation. There is no deposit required at this stage.
      </p>
      {(r.rate_half_day || r.rate_full_day || r.rate_hourly) && (
        <div className="border-t border-[#D2A853]/15 pt-5 mt-5">
          <span className="overline text-[#D2A853] text-[10px]">A note on pricing</span>
          <p className="text-sm text-[#E8E9E3] leading-relaxed mt-3">
            {r.rate_half_day && r.rate_full_day && (
              <>My half-day rate is <span className="text-[#D2A853]">${Number(r.rate_half_day).toLocaleString()}</span> and my full-day rate is <span className="text-[#D2A853]">${Number(r.rate_full_day).toLocaleString()}</span>. </>
            )}
            {r.rate_hourly && (
              <>If your project falls below that range, my hourly rate is <span className="text-[#D2A853]">${Number(r.rate_hourly).toLocaleString()}</span>.</>
            )}
          </p>
        </div>
      )}
    </div>
  );
}

function TabBtn({ active, onClick, children, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`px-5 py-3 overline transition-colors ${
        active ? "text-[#D2A853] border-b-2 border-[#D2A853]" : "text-[#A3A8A4] hover:text-[#E8E9E3]"
      }`}
    >
      {children}
    </button>
  );
}

function DepositWidget({ data, reload }) {
  const [busy, setBusy] = useState(false);
  const status = data.payment?.status;
  const settings = data.settings || {};

  const markPaid = async () => {
    setBusy(true);
    try {
      await http.post("/client/deposit/mark-paid");
      toast.success("Deposit marked as paid — Brian will confirm receipt.");
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const links = [
    { label: "Square", url: settings.square_link, primary: true },
    { label: "PayPal", url: settings.paypal_link },
    { label: "Venmo", url: settings.venmo_link },
  ].filter((l) => l.url);

  return (
    <div className="frame-card p-8" data-testid="deposit-widget">
      <div className="flex items-start justify-between mb-6">
        <div>
          <span className="overline text-[#D2A853]">Deposit</span>
          <h3 className="font-display text-2xl text-[#E8E9E3] mt-2">
            {settings.deposit_amount ? `$${Number(settings.deposit_amount).toFixed(0)} Required` : "Required"}
          </h3>
        </div>
        <DepositStatusIcon status={status} />
      </div>

      {status === "confirmed" ? (
        <div className="flex items-center gap-3 text-[#4CAF50]">
          <CheckCircle2 className="w-5 h-5" strokeWidth={1.4} />
          <span>Deposit received. Booking unlocked.</span>
        </div>
      ) : status === "pending_confirmation" ? (
        <div className="space-y-4">
          <div className="flex items-center gap-3 text-[#E2BC6A]">
            <Clock className="w-5 h-5" strokeWidth={1.4} />
            <span>Pending confirmation by Brian.</span>
          </div>
          <p className="text-sm text-[#A3A8A4]">
            We've notified the studio. You'll receive an SMS + dashboard update once confirmed.
          </p>
        </div>
      ) : (
        <div className="space-y-5">
          <p className="text-sm text-[#A3A8A4] leading-relaxed">
            {settings.deposit_instructions || "Pay the deposit using one of the methods below, then return here and tap 'Mark as Paid'."}
          </p>
          {links.length === 0 ? (
            <div className="border border-[#D2A853]/30 p-4 text-sm text-[#A3A8A4]">
              Brian hasn't published payment links yet. Please reach out for instructions.
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-3">
              {links.map((l) => (
                <a
                  key={l.label}
                  href={l.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`flex items-center justify-between p-4 border ${l.primary ? "border-[#D2A853] bg-[#D2A853]/10" : "border-[#D2A853]/30"} hover:border-[#D2A853] hover:bg-[#D2A853]/10 transition-all`}
                  data-testid={`deposit-link-${l.label.toLowerCase()}`}
                >
                  <span className="overline text-[#E8E9E3]">{l.label}</span>
                  <ExternalLink className="w-4 h-4 text-[#D2A853]" strokeWidth={1.4} />
                </a>
              ))}
            </div>
          )}
          {settings.zelle_info && (
            <div className="text-xs text-[#A3A8A4]">
              <span className="overline text-[#D2A853] mr-2">Zelle</span> {settings.zelle_info}
            </div>
          )}
          <button onClick={markPaid} disabled={busy} className="btn-brass w-full flex items-center justify-center gap-2" data-testid="mark-paid-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
            I've Paid — Mark as Paid
          </button>
        </div>
      )}
    </div>
  );
}

function DepositStatusIcon({ status }) {
  if (status === "confirmed") return <CheckCircle2 className="w-6 h-6 text-[#4CAF50]" strokeWidth={1.4} />;
  if (status === "pending_confirmation") return <Clock className="w-6 h-6 text-[#E2BC6A]" strokeWidth={1.4} />;
  return <Lock className="w-6 h-6 text-[#A3A8A4]" strokeWidth={1.4} />;
}

function BookingWidget({ data, paid, reload }) {
  const appointment = data.appointment;
  const [open, setOpen] = useState(false);
  const [slots, setSlots] = useState([]);
  const [selected, setSelected] = useState([]);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [loadingSlots, setLoadingSlots] = useState(false);

  const loadSlots = async () => {
    setLoadingSlots(true);
    try {
      const { data: r } = await http.get("/client/available-slots");
      setSlots(r.slots || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoadingSlots(false); }
  };

  const toggleSelect = (slotId) => {
    setSelected((cur) => {
      if (cur.includes(slotId)) return cur.filter((x) => x !== slotId);
      if (cur.length >= 3) { toast.error("Pick up to 3 preferred slots"); return cur; }
      return [...cur, slotId];
    });
  };

  const submit = async () => {
    if (selected.length === 0) { toast.error("Pick at least 1 slot"); return; }
    setBusy(true);
    try {
      await http.post("/client/booking/request", { slot_ids: selected, notes });
      toast.success("Request sent. Brian will pick one of your dates.");
      setOpen(false); setSelected([]); setNotes("");
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  // Group slots by date
  const groupedSlots = useMemoSlots(slots);

  return (
    <div className="frame-card p-8" data-testid="booking-widget">
      <div className="flex items-start justify-between mb-6">
        <div>
          <span className="overline text-[#D2A853]">Appointment</span>
          <h3 className="font-display text-2xl text-[#E8E9E3] mt-2">
            {appointment ? "Scheduled" : paid ? "Pick Your Slots" : "Locked"}
          </h3>
        </div>
        {!paid && <Lock className="w-5 h-5 text-[#A3A8A4]" strokeWidth={1.4} />}
      </div>

      {appointment ? (
        <AppointmentDetails
          appointment={appointment}
          settings={data.settings || {}}
          onCancelled={reload}
        />
      ) : !paid ? (
        <p className="text-sm text-[#A3A8A4]">
          Once your deposit is confirmed by Brian, you'll be able to pick from his published openings here.
        </p>
      ) : !open ? (
        <button onClick={() => { setOpen(true); loadSlots(); }} className="btn-outline-brass flex items-center gap-2" data-testid="open-booking-btn">
          <CalendarPlus className="w-4 h-4" strokeWidth={1.4} /> Pick Up to 3 Preferred Slots
        </button>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-[#A3A8A4]">
            Select up to <span className="text-[#D2A853]">3</span> preferred openings. Brian will confirm one and lock it in.
          </p>
          {loadingSlots ? (
            <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-[#D2A853]" /></div>
          ) : groupedSlots.length === 0 ? (
            <div className="text-sm text-[#A3A8A4] p-4 border border-[#D2A853]/20">
              No openings published right now. Check back soon — Brian publishes availability regularly.
            </div>
          ) : (
            <div className="max-h-80 overflow-y-auto space-y-4 pr-2">
              {groupedSlots.map(({ day, items }) => (
                <div key={day}>
                  <div className="overline text-[#D2A853] mb-2 text-[10px]">{day}</div>
                  <div className="space-y-2">
                    {items.map((s) => {
                      const picked = selected.includes(s.id);
                      return (
                        <button
                          key={s.id}
                          onClick={() => toggleSelect(s.id)}
                          className={`w-full text-left p-3 border transition flex items-center justify-between ${
                            picked
                              ? "border-[#D2A853] bg-[#D2A853]/15"
                              : "border-[#D2A853]/20 hover:border-[#D2A853]/50"
                          }`}
                          data-testid={`slot-pick-${s.id}`}
                        >
                          <div>
                            <div className="text-sm text-[#E8E9E3]">{s.label}</div>
                            <div className="text-xs text-[#A3A8A4]">
                              {new Date(s.start).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}
                              {" – "}
                              {new Date(s.end).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}
                            </div>
                          </div>
                          {picked && <CheckCircle2 className="w-5 h-5 text-[#D2A853]" strokeWidth={1.4} />}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
          <textarea
            rows={3}
            placeholder="Notes for Brian (optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="input-line resize-none"
            data-testid="booking-notes"
          />
          <div className="flex gap-3">
            <button onClick={submit} disabled={busy || selected.length === 0} className="btn-brass flex-1 flex items-center justify-center gap-2" data-testid="booking-submit">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} Send Request ({selected.length})
            </button>
            <button onClick={() => { setOpen(false); setSelected([]); }} className="btn-outline-brass">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

function AppointmentDetails({ appointment, settings, onCancelled }) {
  const [busy, setBusy] = useState(false);
  const isTattoo = (appointment.appointment_type || "tattoo").toLowerCase() === "tattoo";
  const isConsult = !isTattoo;
  const consultFmt = appointment.consultation_format || "phone";
  const fmtLabel = consultFmt === "in_person" ? "In-person at the studio"
                 : consultFmt === "video"     ? "Video call"
                 : "Phone call";
  const FmtIcon = consultFmt === "in_person" ? MapPin
                : consultFmt === "video"     ? Video
                : Phone;

  // Hours remaining (for the 72-hr lock display)
  const hoursRemaining = (() => {
    try {
      const ms = new Date(appointment.date).getTime() - Date.now();
      return ms / 3_600_000;
    } catch { return null; }
  })();
  const tattooLocked = isTattoo && hoursRemaining !== null && hoursRemaining < 72;

  // Studio address: tattoo always shows it (when set); consult only if in_person
  const showAddress = !!settings.studio_address && (isTattoo || (isConsult && consultFmt === "in_person"));

  const cancel = async () => {
    if (!window.confirm(isTattoo
      ? "Cancel this tattoo appointment?"
      : "Cancel this consultation?")) return;
    setBusy(true);
    try {
      await http.post(`/client/appointments/${appointment.id}/cancel`);
      toast.success("Appointment cancelled.");
      onCancelled?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="appointment-details">
      {/* Type pill */}
      <div className="flex items-center gap-2">
        <span className="overline text-[#D2A853] text-[10px] px-2 py-1 border border-[#D2A853]/40">
          {isTattoo ? "Tattoo with Brian" : "Consultation with Brian"}
        </span>
        {isConsult && (
          <span className="overline text-[#A3A8A4] text-[10px] inline-flex items-center gap-1.5">
            <FmtIcon className="w-3 h-3" strokeWidth={1.5} /> {fmtLabel}
          </span>
        )}
      </div>

      <div className="text-2xl font-display text-[#D2A853]">
        {new Date(appointment.date).toLocaleString(undefined, { dateStyle: "full", timeStyle: "short" })}
        {settings.timezone_label && <span className="text-sm text-[#A3A8A4] ml-2">({settings.timezone_label})</span>}
      </div>
      <div className="text-sm text-[#A3A8A4]">Duration: {appointment.duration_minutes} minutes</div>
      {appointment.notes && <div className="text-sm text-[#A3A8A4]">{appointment.notes}</div>}

      {/* Custom message */}
      {settings.reminder_custom_message && (
        <p className="text-sm text-[#E8E9E3] leading-relaxed pt-3 border-t border-[#D2A853]/15">
          {settings.reminder_custom_message}
        </p>
      )}

      {/* Studio address */}
      {showAddress && (
        <div className="pt-3 mt-3 border-t border-[#D2A853]/15">
          <span className="overline text-[#D2A853]">Studio Location</span>
          <p className="text-sm text-[#E8E9E3] whitespace-pre-wrap mt-2">{settings.studio_address}</p>
        </div>
      )}

      {/* Policy block */}
      {isTattoo && settings.cancellation_policy && (
        <div className="pt-3 mt-3 border-t border-[#D2A853]/15">
          <span className="overline text-[#D2A853]">Cancellation Policy</span>
          <p className="text-xs text-[#A3A8A4] whitespace-pre-line mt-2 leading-relaxed">{settings.cancellation_policy}</p>
        </div>
      )}
      {isConsult && (
        <p className="text-xs text-[#A3A8A4] mt-2 leading-relaxed">
          Please give at least 3 hours notice if you need to cancel. No penalty for consultation cancellations.
        </p>
      )}

      {/* Action buttons */}
      <div className="flex flex-wrap gap-3 pt-4 border-t border-[#D2A853]/15">
        {tattooLocked ? (
          <div className="text-xs text-[#CF6679] inline-flex items-start gap-2 max-w-md" data-testid="appt-cancel-locked">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" strokeWidth={1.5} />
            <span>
              Within 72 hours — this appointment can no longer be cancelled here. Please contact the studio directly.
            </span>
          </div>
        ) : (
          <button
            onClick={cancel}
            disabled={busy}
            className="text-xs uppercase tracking-wider px-4 py-2 border border-[#A3A8A4]/40 text-[#A3A8A4] hover:border-[#CF6679] hover:text-[#CF6679] transition disabled:opacity-50"
            data-testid="appt-cancel-btn"
          >
            {busy ? "Cancelling…" : "Cancel Appointment"}
          </button>
        )}
        <a
          href="#booking-widget"
          onClick={(e) => { e.preventDefault(); onCancelled?.(); window.scrollTo({ top: 0, behavior: "smooth" }); }}
          className="text-xs uppercase tracking-wider px-4 py-2 border border-[#D2A853]/40 text-[#D2A853] hover:bg-[#D2A853]/10 transition"
          data-testid="appt-schedule-another"
        >
          Schedule Another Appointment
        </a>
      </div>
    </div>
  );
}

function useMemoSlots(slots) {
  return React.useMemo(() => {
    const map = new Map();
    for (const s of slots) {
      const key = new Date(s.start).toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" });
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(s);
    }
    return Array.from(map.entries()).map(([day, items]) => ({ day, items }));
  }, [slots]);
}

function ReferencesWidget({ files }) {
  if (!files || files.length === 0) return null;
  return (
    <div className="frame-card p-8" data-testid="references-widget">
      <div className="flex items-center gap-3 mb-6">
        <ImageIcon className="w-4 h-4 text-[#D2A853]" strokeWidth={1.4} />
        <span className="overline text-[#D2A853]">Your References</span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {files.map((f) => (
          <a
            key={f.id}
            href={fileUrl(f.id)}
            target="_blank"
            rel="noopener noreferrer"
            className="aspect-square border border-[#D2A853]/20 hover:border-[#D2A853]/60 transition overflow-hidden block"
            data-testid={`reference-${f.id}`}
          >
            <img src={fileUrl(f.id)} alt={f.filename} className="w-full h-full object-cover" />
          </a>
        ))}
      </div>
    </div>
  );
}

function ProjectCard({ intake }) {
  if (!intake) return null;
  return (
    <div className="frame-card p-8" data-testid="project-card">
      <div className="flex items-center gap-3 mb-4">
        <FileText className="w-4 h-4 text-[#D2A853]" strokeWidth={1.4} />
        <span className="overline text-[#D2A853]">Project Brief</span>
      </div>
      <p className="text-sm text-[#E8E9E3] leading-relaxed mb-4 whitespace-pre-wrap">{intake.project_description}</p>
      <div className="space-y-2 pt-4 border-t border-[#D2A853]/15">
        <Detail label="Placement" value={intake.placement} />
        <Detail label="Size" value={intake.size} />
        {intake.budget && <Detail label="Budget" value={intake.budget} />}
      </div>
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div className="flex justify-between text-sm">
      <span className="overline text-[#A3A8A4]">{label}</span>
      <span className="text-[#E8E9E3]">{value}</span>
    </div>
  );
}

function NotesCard({ notes }) {
  if (!notes || notes.length === 0) return (
    <div className="frame-card p-8" data-testid="notes-card-empty">
      <span className="overline text-[#D2A853]">Notes from Brian</span>
      <p className="text-sm text-[#A3A8A4] mt-3">No notes yet.</p>
    </div>
  );
  return (
    <div className="frame-card p-8" data-testid="notes-card">
      <span className="overline text-[#D2A853]">Notes from Brian</span>
      <div className="space-y-3 mt-4">
        {notes.map((n) => (
          <div key={n.id} className="border-l-2 border-[#D2A853]/40 pl-4 py-1">
            <p className="text-sm text-[#E8E9E3] whitespace-pre-wrap">{n.text}</p>
            <span className="text-xs text-[#A3A8A4] mt-1 block">
              {new Date(n.created_at).toLocaleDateString()}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function MessagesPanel({ messages, reload }) {
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    setBusy(true);
    try {
      await http.post("/client/messages", { body });
      setBody("");
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };
  return (
    <div className="frame-card p-8" data-testid="messages-panel">
      <span className="overline text-[#D2A853]">Direct Line to Brian</span>
      <div className="mt-6 max-h-96 overflow-y-auto space-y-3 mb-6 pr-2">
        {messages.length === 0 && <p className="text-sm text-[#A3A8A4]">No messages yet. Start a conversation.</p>}
        {messages.map((m) => (
          <div
            key={m.id}
            className={`flex ${m.from_role === "client" ? "justify-end" : "justify-start"}`}
          >
            <div
              className={`max-w-[80%] p-3 ${
                m.from_role === "client"
                  ? "bg-[#D2A853]/15 border border-[#D2A853]/40 text-[#E8E9E3]"
                  : "bg-[#1A241E] border border-[#D2A853]/20 text-[#E8E9E3]"
              }`}
            >
              <p className="text-sm whitespace-pre-wrap">{m.body}</p>
              <span className="text-[10px] text-[#A3A8A4] mt-1 block">
                {new Date(m.created_at).toLocaleString()}
              </span>
            </div>
          </div>
        ))}
      </div>
      <form onSubmit={send} className="flex gap-3 pt-6 border-t border-[#D2A853]/15">
        <input
          className="input-line flex-1"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Write a message..."
          data-testid="client-message-input"
        />
        <button type="submit" disabled={busy} className="btn-brass flex items-center gap-2" data-testid="client-message-send">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send
        </button>
      </form>
    </div>
  );
}

function AccountPanel({ user }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await http.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated.");
      setCurrent(""); setNext("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
      <div className="frame-card p-8">
        <span className="overline text-[#D2A853]">Account Details</span>
        <div className="mt-6 space-y-3">
          <Detail label="Name" value={user.name} />
          <Detail label="Email" value={user.email} />
          <Detail label="Phone" value={user.phone || "—"} />
        </div>
      </div>
      <div className="frame-card p-8">
        <span className="overline text-[#D2A853]">Change Password</span>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <input type="password" placeholder="Current password" value={current} onChange={(e) => setCurrent(e.target.value)} className="input-line" data-testid="acct-current-pw" />
          <input type="password" placeholder="New password (8+ chars)" value={next} onChange={(e) => setNext(e.target.value)} className="input-line" data-testid="acct-new-pw" />
          <button type="submit" disabled={busy} className="btn-brass w-full flex items-center justify-center gap-2" data-testid="acct-save-pw">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Update Password
          </button>
        </form>
      </div>
    </div>
  );
}
